<template>
  <div>
    <p>
      Fyll inn nødvendig informasjon relatert til leieobjektet
      {{ rentalObject.address.street }} under utleier {{ landlordName }}.
    </p>

    <div class="row mt-4">
      <div class="col-12 col-md-6">
        <ValidationObserver v-slot="{ invalid }">
          <!-- <pre>{{ issues }}</pre> -->
          <!-- / Apartment Number -->
          <ValidationProvider slim ref="apartmentNumberRef" name="Leilighetsnummer" v-slot="{ errors }">
            <hl-Select
              v-if="hasApartmentNumberIssue"
              label="Leilighetsnummer"
              v-model="details.apartmentNumber"
              :options="getSelectMenuOptions(hasApartmentNumberIssue.newValue)"
              :placeholder="hasApartmentNumberIssue.previousValue"
              :placeholderValue="hasApartmentNumberIssue.previousValue"
              :error="errors[0]"
              errorVariant="warning"
            />
            <hl-Input
              v-else
              class="mt-4"
              label="Leilighetsnummer"
              :value="rentalObject.details.apartmentNumber"
              :error="errors[0]"
              errorVariant="warning"
              :disabled="!!rentalObject.details.apartmentNumber || !hasStreetIssue"
            />
          </ValidationProvider>

          <!-- / Apartment Number -->

          <!-- Address street -->
          <ValidationProvider
            v-if="hasStreetIssue"
            slim
            ref="addressRef"
            name="Adresse"
            rules="required"
            v-slot="{ errors }"
          >
            <hl-Select
              v-if="hasStreetOptions"
              label="Adresse"
              class="mt-4"
              v-model="address.street"
              :options="getSelectMenuOptions(hasStreetIssue.newValue)"
              :placeholder="hasStreetIssue.previousValue"
              :placeholderValue="hasStreetIssue.previousValue"
              :error="errors[0]"
              errorVariant="warning"
            />

            <hl-Input
              v-else
              label="Adresse"
              class="mt-4"
              v-model="address.street"
              :error="errors[0]"
              errorVariant="warning"
            />
          </ValidationProvider>

          <hl-Input v-else disabled class="mt-4" label="Adresse" :value="rentalObject.address.street" />
          <!-- / Address street -->

          <!-- PostalZipCode -->
          <ValidationProvider
            v-if="hasPostalZipCodeIssue"
            slim
            :mode="custom"
            ref="postalZipRef"
            name="Postnummer"
            :rules="`required|digits:4`"
            v-slot="{ errors }"
          >
            <hl-Input
              class="mt-4"
              label="Postnummer"
              placeholder="8410"
              @blur="validatePostalAreaFields"
              @input="fetchPostalArea"
              v-model="address.postalZipcode"
              :error="errors[0]"
              errorVariant="warning"
            />
          </ValidationProvider>

          <hl-Input
            v-else
            disabled
            class="mt-4"
            label="Postnummer"
            placeholder="8410"
            :value="rentalObject.address.postalZipcode"
          />
          <!-- PostalZipCode -->

          <ValidationProvider ref="postalAreaRef" name="Poststed">
            <hl-Input class="mt-4" label="Poststed" disabled :value="address.postalArea.name" />
          </ValidationProvider>

          <hl-Button
            @click="submitRentalObject"
            :loading="loading"
            class="mt-4"
            :disabled="invalid || loading"
            variant="primary"
          >
            Bekreft
          </hl-Button>
        </ValidationObserver>
      </div>

      <div class="col-12 col-md-6 mt-5 mt-md-0">
        <div class="rental-object-summary">
          <p class="mb-0">En liten påminnelse om hvilket leieobjekt vi spør om:</p>
          <div class="my-4">
            <img
              v-if="rentalObject.details.profilePictureUri"
              class="img-fluid border-radius"
              :src="rentalObject.details.profilePictureUri"
              alt=""
            />
            <img
              v-else
              class="img-fluid border-radius"
              :src="require('../../../../modules/husleie-framework/img/graphic-elements/maps/map_marker.png')"
              alt=""
            />
          </div>

          <div class="my-4">
            {{ rentalObject.details.areaSize }}kvm
            <span class="lowercase">
              {{ categoryMap[rentalObject.details.type] }}
              ({{ rentalObject.details.furnished }})
            </span>
            <template v-if="rentalObjectDetailsRooms.length > 0">
              med
              <span class="comma" v-for="(room, i) in rentalObjectDetailsRooms" :key="i">{{
                mapFacilities(room.name)
              }}</span>
            </template>
          </div>

          <div class="my-4">
            <p>Leietaker(e):</p>
            <ul v-for="(tenancy, i) in rentalObject.tenancies" :key="i" class="m-0">
              <li class="p-0">
                {{ tenancy.tenants.map(tenant => tenant.name)[0] }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  rentalObjectCategoryMap as categoryMap,
  rentalObjectFacilityList as facilityList,
} from '@/lib/rentalObjectHelpers';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import husleieapi from '@modules/husleie-api';

export default {
  components: {
    'hl-Select': Select,
    'hl-Input': Input,
    'hl-Button': Button,
  },

  props: {
    issues: {
      type: Array,
      required: true,
    },
    landlordId: {
      type: [String, Number],
      required: true,
    },
    landlordName: {
      type: String,
      default: 'Landlord name',
    },
    rentalObject: {
      type: Object,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      address: {
        street: '',
        postalZipcode: '',
        postalArea: {},
      },
      details: {
        apartmentNumber: '',
      },
      loading: false,
      categoryMap,
      facilityList,
    };
  },

  computed: {
    hasApartmentNumberIssue() {
      return this.issues.find(issue => issue.name === 'apartmentNumber');
    },

    hasStreetIssue() {
      return this.issues.find(issue => issue.name === 'street');
    },

    hasStreetOptions() {
      return this.hasStreetIssue ? this.hasOptions(this.hasStreetIssue.newValue) : false;
    },

    hasPostalZipCodeIssue() {
      return this.issues.find(issue => issue.name === 'postalZipcode');
    },

    rentalObjectDetailsRooms() {
      return this.rentalObject.details.rooms.filter(room => room.count > 0);
    },
  },

  methods: {
    custom() {
      return { on: ['input'] };
    },

    hasOptions(newVal) {
      if (newVal) {
        if (Array.isArray(newVal) && newVal.length) {
          return true;
        } else if (typeof newVal === 'object' && Object.keys(newVal).length) {
          return true;
        }
      }
      return false;
    },

    getSelectMenuOptions(newVal) {
      if (newVal.length === 0) return;
      if (this.hasOptions(newVal)) {
        const opts = [];
        for (const opt of newVal) {
          opts.push({
            name: opt,
            value: opt,
          });
        }
        return opts;
      } else {
        const opts = [];
        opts.push({
          name: newVal,
          value: newVal,
        });
        return opts;
      }
    },

    mapFacilities(roomName) {
      const match = this.facilityList.find(facility => facility.value === roomName);
      if (match == null) return roomName;
      return match.name;
    },

    async submitRentalObject() {
      this.loading = true;
      try {
        const {
          postalArea: { name: postalArea },
          ...address
        } = this.address;
        const data = {
          details: this.details,
          address: { ...address, postalArea },
          acknowledgementToken: this.token,
        };
        await this.patchRentalObject({
          landlordId: this.landlordId,
          rentalObjectId: this.rentalObject.id,
          data,
        });
      } catch (error) {
        this.$toast('Kunne ikke lagre', 'error');
      }
      this.loading = false;
    },

    fetchPostalArea(e) {
      const axios = require('axios');

      // Make a request for a user with a given ID
      if (e.match(/^\d{4}$/)) {
        axios
          .get('/api/poststed/' + e)
          .then(response => {
            const {
              data: { poststed: name, error = 0 },
            } = response;

            this.address.postalArea = { name, error };
            if (error) {
              this.validatePostalAreaFields();
            }
          })
          .catch(function (error) {
            console.error(error);
          });
      }
    },

    validatePostalAreaFields() {
      if (this.address.postalArea.error) {
        const result = {
          errors: ['Ugyldig postnummer'],
          valid: false,
          failedRules: {},
        };

        const refs = ['postalZipRef', 'postalAreaRef'];
        refs.forEach(ref => this.$refs[ref].applyResult(result));
      }
    },

    async patchRentalObject({ landlordId, rentalObjectId, data }) {
      const opts = {
        rentalObject: data,
      };
      const hlApi = new husleieapi();

      await hlApi.call('RentalObjectApi.patchRentalObject', landlordId, rentalObjectId, opts, error => {
        if (error) {
          throw new Error('error', error);
        } else {
          this.$emit('updated');
        }
      });
    },
  },

  mounted() {
    // pre-populate fields with their previous values ( if we have them )
    if (this.hasApartmentNumberIssue) {
      this.details.apartmentNumber = this.hasApartmentNumberIssue.previousValue;
      this.$nextTick(() => {
        this.$refs.apartmentNumberRef.applyResult({
          errors: [this.hasApartmentNumberIssue.error],
          valid: false,
          failedRules: {},
        });
      });
    } else {
      this.details.apartmentNumber = this.rentalObject.details.apartmentNumber;
      if (!this.details.apartmentNumber && this.hasStreetIssue) {
        this.$nextTick(() => {
          this.$refs.apartmentNumberRef.applyResult({
            errors: ['Bekreft leilighetsnummer'],
            valid: false,
            failedRules: {},
          });
        });
      }
    }

    if (this.hasPostalZipCodeIssue) {
      this.address.postalZipcode = this.hasPostalZipCodeIssue.previousValue;
      this.fetchPostalArea(this.address.postalZipcode); // validation errors set by fetchPostalArea()
    } else {
      this.address.postalZipcode = this.rentalObject.address.postalZipcode;
      if (!this.address.postalArea || !this.address.postalArea.name) {
        this.fetchPostalArea(this.address.postalZipcode); // validation errors set by fetchPostalArea()
      }
    }

    if (this.hasStreetIssue) {
      this.address.street = this.hasStreetIssue.previousValue;
      this.$nextTick(() => {
        this.$refs.addressRef.applyResult({
          errors: [this.hasStreetIssue.error],
          valid: false,
          failedRules: {},
        });
      });
    } else {
      this.address.street = this.rentalObject.address.street;
    }
  },
};
</script>

<style scoped lang="scss">
.rental-object-summary {
  border: 1px solid $gray-400;
  border-radius: $border-radius-medium;
  padding: 2rem;

  .comma {
    text-transform: lowercase;

    &:not(:first-child):before {
      content: ', ';
    }

    &:last-child:after {
      content: '.';
    }
  }

  .lowercase {
    text-transform: lowercase;
  }
}

.border-radius {
  border-radius: $border-radius-medium;
}
</style>
